import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, LOCALE_ID, NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatMomentDateModule } from '@angular/material-moment-adapter';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DATE_FORMATS, MAT_DATE_LOCALE, MAT_HAMMER_OPTIONS } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Router, RouterModule, Routes } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import { LeafletModule } from '@asymmetrik/ngx-leaflet';
import { LeafletMarkerClusterModule } from '@asymmetrik/ngx-leaflet-markercluster';
import { AuthenticationService } from '@core/authentication/authentication.service';
import { CustomSnackBarModule } from '@core/components/custom-snackbar/custom-snackbar.module';
import { NetPromoterScoreModalModule } from '@core/components/net-promoter-score-modal/net-promoter-score-modal.module';
import { MOMENT_DATE_FORMATS, USER_LANGUAGE } from '@core/consts/date';
import { AuthenticatedGuard } from '@core/guard/authenticated.guard';
import { AuthErrorInterceptor } from '@core/interceptor/auth-error.interceptor';
import { ServerErrorInterceptor } from '@core/interceptor/server-error.interceptor';
import { LocaleService } from '@core/services/locale.service';
import { IndicationPopUpModule } from '@core/services/navigation/indication-pop-up/indication-pop-up.module';
import { SocketService } from '@core/services/socket.service';
import { UpdateService } from '@core/services/update/update.service';
import { FuseProgressBarModule, FuseSidebarModule } from '@fuse/components';
import { ConfirmDialogModule } from '@fuse/components/confirm-dialog/confirm-dialog.module';
import { FuseModule } from '@fuse/fuse.module';
import { PipesModule } from '@fuse/pipes/pipes.module';
import { FuseSplashScreenService } from '@fuse/services/splash-screen.service';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { FuseSharedModule } from '@fuse/shared.module';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DateRangePickerModule } from '@syncfusion/ej2-angular-calendars';
import { environment } from 'environments/environment';
import 'hammerjs';
import { sentry } from 'sentry';
import { AppComponent } from './app.component';
import { fuseConfig } from './config';
import { AccessTokenInterceptor } from './core/interceptor/access-token.interceptor';
import { PaginatorI18n } from './core/utils/functions/PaginatorI18n';
import { GraphQLModule } from './graphql.module';
import { VerticalLayoutModule } from './layout/vertical/layout.module';
import { Error404Component } from './main/404/error-404.component';
import { Error404Module } from './main/404/error-404.module';
import { ApprovalsEquipmentsModule } from './main/approvals-equipments/approvals-equipments.module';
import { EquipmentsModule } from './main/equipments/equipments.module';
import { LocationsGroupsModule } from './main/locations-groups/locations-groups.module';
import { LocationsModule } from './main/locations/locations.module';
import { LoginModule } from './main/login/login.module';
import { MaintenancesModule } from './main/maintenances/maintenances.module';
import { RecurrencesModule } from './main/recurrences/recurrences.module';
import { CompanyComparisonModule } from './main/reports/company-comparison/company-comparison.module';
import { CompanyRankingModule } from './main/reports/company-ranking/company-ranking.module';
import { DeadlinesModule } from './main/reports/deadlines/deadlines.module';
import { DashboardEquipmentsModule } from './main/reports/equipments/dashboard-equipments.module';
import { DashboardFormsModule } from './main/reports/forms/dashboard-forms.module';
import { DashboardMaintenancesModule } from './main/reports/maintenances/dashboard-maintenances.module';
import { RatingsModule } from './main/reports/ratings/ratings.module';
import { DashboardStorageMovementsModule } from './main/reports/storage-movements/storage-movements.module';
import { VolumetryModule } from './main/reports/volumetry/volumetry.module';
import { GroupsModule } from './main/settings/accounts/groups/groups.module';
import { UsersModule } from './main/settings/accounts/users/users.module';
import { SegmentsModule } from './main/settings/registers/segments/segments.module';

const routes: Routes = [
  {
    path: '',
    redirectTo: 'login',
    pathMatch: 'full'
  },
  {
    path: 'relatorios/orcamentos',
    canActivate: [AuthenticatedGuard],
    loadChildren: () => import('./main/reports/quotations/dashboard-quotations.module').then((m) => m.DashboardQuotationsModule),
    data: { title: 'CORE.SERVICES.NAVIGATION.REPORTS.TITLES.QUOTATIONS' }
  },
  {
    path: 'manutencoes/tipos',
    canActivate: [AuthenticatedGuard],
    loadChildren: () => import('./main/settings/registers/maintenances-types/maintenances-types.module').then((m) => m.MaintenancesTypesModule),
    data: { title: 'CORE.SERVICES.NAVIGATION.SETTINGS.MAINTENANCES_TYPES' }
  },
  {
    path: 'manutencoes/mapa',
    canActivate: [AuthenticatedGuard],
    loadChildren: () => import('./main/maintenances-map/maintenances-map.module').then((m) => m.MaintenancesMapModule),
    data: { title: 'CORE.SERVICES.NAVIGATION.MAINTENANCES.MAP' }
  },
  {
    path: 'orcamentos',
    canActivate: [AuthenticatedGuard],
    loadChildren: () => import('./main/quotations/quotations.module').then((m) => m.QuotationsModule),
    data: { title: 'CORE.SERVICES.NAVIGATION.QUOTATIONS' }
  },
  {
    path: 'equipamentos/tipos',
    canActivate: [AuthenticatedGuard],
    loadChildren: () => import('./main/settings/registers/equipments-types/equipments-types.module').then((m) => m.EquipmentsTypesModule),
    data: { title: 'CORE.SERVICES.NAVIGATION.EQUIPMENTS.TYPES' }
  },
  {
    path: 'configuracoes/conta',
    canActivate: [AuthenticatedGuard],
    loadChildren: () => import('./main/settings/accounts/preferences/account-preferences.module').then((m) => m.AccountPreferencesModule),
    data: { title: 'CORE.SERVICES.NAVIGATION.SETTINGS.ACCOUNT' }
  },
  {
    path: 'problemas',
    canActivate: [AuthenticatedGuard],
    loadChildren: () => import('./main/settings/registers/problems/problems.module').then((m) => m.ProblemsModule),
    data: { title: 'CORE.SERVICES.NAVIGATION.SETTINGS.PROBLEMS' }
  },
  {
    path: 'etiquetas',
    canActivate: [AuthenticatedGuard],
    loadChildren: () => import('./main/settings/registers/labels/labels.module').then((m) => m.LabelsModule),
    data: { title: 'CORE.SERVICES.NAVIGATION.SETTINGS.LABELS' }
  },
  {
    path: 'contratos-de-prazos',
    canActivate: [AuthenticatedGuard],
    loadChildren: () => import('./main/settings/registers/deadlines-contracts/deadlines-contracts.module').then((m) => m.DeadlinesContractsModule),
    data: { title: 'CORE.SERVICES.NAVIGATION.SETTINGS.DEADLINES_CONTRACTS' }
  },
  {
    path: 'formularios',
    canActivate: [AuthenticatedGuard],
    loadChildren: () => import('./main/settings/registers/forms/forms.module').then((m) => m.FormsModule),
    data: { title: 'CORE.SERVICES.NAVIGATION.SETTINGS.FORMS' }
  },
  {
    path: 'empresas',
    canActivate: [AuthenticatedGuard],
    loadChildren: () => import('./main/settings/accounts/companies/companies.module').then((m) => m.CompaniesModule),
    data: { title: 'CORE.SERVICES.NAVIGATION.SETTINGS.COMPANIES' }
  },
  {
    path: 'importacoes',
    canActivate: [AuthenticatedGuard],
    loadChildren: () => import('./main/settings/imports/imports.module').then((m) => m.ImportsModule),
    data: { title: 'CORE.SERVICES.NAVIGATION.SETTINGS.IMPORTS' }
  },
  {
    path: 'desenvolvedor/api-de-integracao',
    canActivate: [AuthenticatedGuard],
    loadChildren: () => import('./main/settings/developer/integration-api/integration-api.module').then((m) => m.IntegrationApiModule),
    data: { title: 'CORE.SERVICES.NAVIGATION.SETTINGS.INTEGRATION_API' }
  },
  {
    path: 'desenvolvedor/historico-de-eventos',
    canActivate: [AuthenticatedGuard],
    loadChildren: () => import('./main/settings/developer/integration-logs/integration-logs.module').then((m) => m.IntegrationLogsModule),
    data: { title: 'CORE.SERVICES.NAVIGATION.SETTINGS.INTEGRATION_LOGS' }
  },
  {
    path: 'perfil',
    canActivate: [AuthenticatedGuard],
    loadChildren: () => import('./main/settings/profile/profile.module').then((m) => m.ProfileModule),
    data: { title: 'CORE.SERVICES.NAVIGATION.SETTINGS.PROFILE' }
  },
  {
    path: 'arvore-de-ativos',
    canActivate: [AuthenticatedGuard],
    loadChildren: () => import('./main/assets-tree/assets-tree.module').then((m) => m.AssetsTreeModule),
    data: { title: 'CORE.SERVICES.NAVIGATION.ASSETS_TREE' }
  },
  {
    path: '404',
    component: Error404Component,
    data: { title: 'CORE.SERVICES.NAVIGATION.TITLES.NOT_FOUND' }
  },
  {
    path: '**',
    redirectTo: '404'
  }
];

@NgModule({
  declarations: [AppComponent],
  imports: [
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: environment.SERVICE_WORKER,
      scope: location.pathname
    }),
    RouterModule.forRoot(routes, {
      useHash: true,
      enableTracing: false
    }),
    FuseModule.forRoot(fuseConfig),
    LeafletModule.forRoot(),
    LeafletMarkerClusterModule.forRoot(),
    TranslateModule.forRoot(),
    BrowserModule,
    NetPromoterScoreModalModule,
    BrowserAnimationsModule,
    CommonModule,
    HttpClientModule,
    MatMomentDateModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    FuseProgressBarModule,
    FuseSharedModule,
    FuseSidebarModule,
    PipesModule,
    ConfirmDialogModule,
    VerticalLayoutModule,
    LoginModule,
    DashboardMaintenancesModule,
    DashboardStorageMovementsModule,
    VolumetryModule,
    DeadlinesModule,
    RatingsModule,
    CompanyComparisonModule,
    CompanyRankingModule,
    MaintenancesModule,
    LocationsModule,
    EquipmentsModule,
    LoginModule,
    ApprovalsEquipmentsModule,
    RecurrencesModule,
    UsersModule,
    GroupsModule,
    LocationsGroupsModule,
    SegmentsModule,
    FormsModule,
    DashboardEquipmentsModule,
    Error404Module,
    GraphQLModule,
    CustomSnackBarModule,
    DateRangePickerModule,
    IndicationPopUpModule,
    DashboardFormsModule
  ],
  providers: [
    {
      provide: MAT_DATE_LOCALE,
      useValue: USER_LANGUAGE
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: MOMENT_DATE_FORMATS
    },
    {
      provide: HTTP_INTERCEPTORS,
      multi: true,
      useClass: AccessTokenInterceptor,
    },
    {
      provide: HTTP_INTERCEPTORS,
      multi: true,
      useClass: AuthErrorInterceptor,
    },
    {
      provide: HTTP_INTERCEPTORS,
      multi: true,
      useClass: ServerErrorInterceptor,
    },
    {
      provide: MAT_HAMMER_OPTIONS,
      useValue: {
        cssProps: {
          userSelect: true
        }
      },
    },
    {
      provide: sentry.TraceService,
      deps: [Router],
    },
    {
      provide: ErrorHandler,
      useValue: sentry.createErrorHandler({
        showDialog: false,
        logErrors: true
      }),
    },
    {
      provide: MatPaginatorIntl,
      deps: [TranslateService, FuseTranslationLoaderService],
      useFactory: (translateService: TranslateService, translateLoaderService: FuseTranslationLoaderService) => new PaginatorI18n(translateService, translateLoaderService).getPaginatorIntl()
    },
    {
      provide: LOCALE_ID,
      deps: [LocaleService],
      useFactory: (localeService: LocaleService) => localeService.language()
    },
    {
      provide: APP_INITIALIZER,
      multi: true,
      deps: [AuthenticationService, SocketService, FuseSplashScreenService, UpdateService, sentry.TraceService],
      useFactory: (authenticationService: AuthenticationService) => {
        return async () => {
          await authenticationService.refreshTokenAndPermissions();
          window.dispatchEvent(new Event('refresh-notification-count'));
        };
      }
    },

  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
